// Active-well memory — the "current well" the user is browsing. Navigating to any
// page for a well marks it active (useMarkActiveWell); the Sidebar shows it as an
// indicator, and launching a report from the Sidebar defaults to it instead of
// popping the full pad→well→job selector (PO 2026-08-04). This is the OXBO-x port
// of BIOT-k's active-bore affordance.
//
// IDS ONLY (padId, wellId, optional jobId) — never full objects. The Sidebar
// resolves names + jobs fresh from the spine on every read (getWellById / the live
// wells array), so the pointer can never go stale against a store mutation (the
// exact staleness the BIOT-k reportContext slice's own comment warns about). A
// pointer at a since-archived/deleted well simply resolves to nothing → the
// indicator hides and report launch falls back to the full selector.
//
// SESSION-scoped (sessionStorage), deliberately — same rationale as
// features/timeline/activeJobContext.ts: for the multi-tenant "promiscuous user"
// (a core persona, CLAUDE.md multi-tenancy hard rule) a cross-session memory could
// re-surface a valid-but-wrong-ORG well from a prior visit. Per-session scope +
// resolve-against-the-live-store-on-read avoids that. This is a UI affordance, not
// a data record — no engineering/UOM data here (Charter Abs-2/3 N/A).
//
// REACTIVE — a module-level in-memory value is the authority, mirrored to
// sessionStorage; setActiveWell/clearActiveWell notify subscribers so the Sidebar
// re-renders the instant navigation changes the active well (useActiveWell wraps
// this in useSyncExternalStore). getSnapshot returns a STABLE reference between
// writes (required by useSyncExternalStore — a fresh object each call would loop).

/** The active-well pointer. `jobId` is the well's remembered active job (set when a
 *  job is picked or is the well's only one); null = well active, no job committed. */
export interface ActiveWell {
  padId: string;
  wellId: string;
  jobId: string | null;
}

const ACTIVE_WELL_KEY = "oxbo-active-well";

// `undefined` = not yet hydrated from sessionStorage; `null` = hydrated, no active
// well. The in-memory value is the authority (getSnapshot returns it by reference).
let current: ActiveWell | null | undefined;
const listeners = new Set<() => void>();

function hydrate(): ActiveWell | null {
  try {
    const raw = sessionStorage.getItem(ACTIVE_WELL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ActiveWell>;
    if (typeof parsed.padId === "string" && typeof parsed.wellId === "string") {
      return {
        padId: parsed.padId,
        wellId: parsed.wellId,
        jobId: typeof parsed.jobId === "string" ? parsed.jobId : null,
      };
    }
    return null;
  } catch {
    return null;
  }
}

function ensureHydrated(): ActiveWell | null {
  if (current === undefined) current = hydrate();
  return current;
}

function emit(): void {
  for (const listener of listeners) listener();
}

/** The current active-well pointer (ids only), or null. */
export function getActiveWell(): ActiveWell | null {
  return ensureHydrated();
}

/** useSyncExternalStore subscribe — Sidebar re-renders on active-well change. */
export function subscribeActiveWell(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** useSyncExternalStore snapshot — a STABLE reference between writes. */
export function getActiveWellSnapshot(): ActiveWell | null {
  return ensureHydrated();
}

/** Mark a well active (replaces any prior pointer). Navigating to a different well
 *  resets jobId to null via the caller; a scoped/modal job pick sets it. */
export function setActiveWell(next: ActiveWell): void {
  current = next;
  try {
    sessionStorage.setItem(ACTIVE_WELL_KEY, JSON.stringify(next));
  } catch {
    // storage disabled / quota — non-fatal; the in-memory value still drives the UI
  }
  emit();
}

/** Clear the active well (the indicator's × button). */
export function clearActiveWell(): void {
  current = null;
  try {
    sessionStorage.removeItem(ACTIVE_WELL_KEY);
  } catch {
    // non-fatal
  }
  emit();
}

/** TEST-ONLY reset of the in-memory authority (jsdom sessionStorage persists across
 *  a test file's cases). Not for app code. */
export function __resetActiveWellForTests(): void {
  current = undefined;
  listeners.clear();
}
